import { memo, useEffect, useMemo, useState } from 'react';
import { Box, InputLabel, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseDeviceModal from '~/components/common/base-device-modal/BaseDeviceModal';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import personApi from '~/apis/person';
import deviceApi from '~/apis/device';
import { handleApiError, notify } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';

type ExchangeDeviceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  personId: number;
  cccd: string;
  fullName: string;
  currentImei: string;
  onSuccess?: () => void;
};

const useExchangeDeviceApi = () => {
  const [isLoading, setIsLoading] = useState(false);

  const exchangeDevice = async (personId: number, imei: string, reason: string) => {
    setIsLoading(true);
    try {
      await personApi.changeDevice(personId, { imei, reason });
      return true;
    } catch (error) {
      handleApiError(error, true);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { exchangeDevice, isLoading };
};

const useUnassignedImeis = (isOpen: boolean) => {
  const [imeiOptions, setImeiOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      try {
        const response = await deviceApi.getUnassignedImeis({});
        setImeiOptions(response.data?.result ?? []);
      } catch (error) {
        handleApiError(error);
        setImeiOptions([]);
      }
    })();
  }, [isOpen]);

  return { imeiOptions };
};

const ExchangeDeviceModal = ({
  isOpen,
  onClose,
  personId,
  cccd,
  fullName,
  currentImei,
  onSuccess,
}: ExchangeDeviceModalProps) => {
  const { t } = useTranslation('admin/common');
  const [newImei, setNewImei] = useState('');
  const [reason, setReason] = useState('');

  const { exchangeDevice, isLoading } = useExchangeDeviceApi();
  const { imeiOptions } = useUnassignedImeis(isOpen);

  const validImeiOptions = useMemo(
    () => imeiOptions.filter((item) => item !== currentImei),
    [imeiOptions, currentImei]
  );

  const selectOptions: IMasterDataItem[] = useMemo(
    () => validImeiOptions.map((item) => ({ id: item, name: item })),
    [validImeiOptions]
  );

  useEffect(() => {
    if (!isOpen) {
      setNewImei('');
      setReason('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setNewImei('');
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    const imeiValue = newImei.trim();
    const reasonValue = reason.trim();

    if (!imeiValue) {
      notify('error', t('deviceModal.selectDeviceWarning'));
      return;
    }

    if (!reasonValue) {
      notify('error', t('deviceModal.reasonRequired'));
      return;
    }

    const isSuccess = await exchangeDevice(personId, imeiValue, reasonValue);
    if (isSuccess) {
      notify('success', t('deviceModal.exchangeSuccess'));
      onSuccess?.();
      handleClose();
    }
  };

  const handleChangeNewImei = (event: any) => {
    setNewImei(String(event.target.value ?? ''));
  };

  return (
    <BaseDeviceModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('deviceModal.exchangeTitle')}
      cccd={cccd}
      fullName={fullName}
      onConfirm={handleConfirm}
      isConfirmLoading={isLoading}
      cancelText={t('cancel')}
      confirmText={t('deviceModal.saveConfirm')}
    >
      <Box className='flex flex-col gap-4 w-full'>
        <Box className='flex flex-col gap-2 w-full'>
          <InputLabel
            shrink={false}
            className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
          >
            {t('deviceModal.currentImeiLabel')}
          </InputLabel>
          <Box className='w-full h-12 rounded-[12px] px-[14px] flex items-center border-2 border-[#D7DEE7] bg-[#D7DEE7] font-normal text-sm leading-[140%] tracking-[0.02em] text-[#7B8794] select-text'>
            {currentImei || '—'}
          </Box>
        </Box>

        <Box className='flex flex-col gap-2 w-full'>
          <InputLabel
            shrink={false}
            className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
          >
            {t('deviceModal.newImeiLabel')}
          </InputLabel>
          <BaseSelect<IMasterDataItem>
            name='newImei'
            value={newImei}
            onChange={handleChangeNewImei}
            options={selectOptions}
            placeholder={t('deviceModal.selectDevicePlaceholder')}
            isSearchEnable
          />
        </Box>

        <Box className='flex flex-col gap-2 w-full'>
          <InputLabel
            shrink={false}
            className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
          >
            {t('deviceModal.exchangeReasonLabel')}
          </InputLabel>
          <TextField
            multiline
            minRows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('deviceModal.reasonPlaceholder')}
            className='[&_.MuiOutlinedInput-root]:min-h-[96px] [&_.MuiOutlinedInput-root]:rounded-[12px] [&_.MuiOutlinedInput-root]:bg-white [&_.MuiOutlinedInput-root]:items-start [&_.MuiOutlinedInput-root]:font-normal [&_.MuiOutlinedInput-root]:text-sm [&_.MuiOutlinedInput-root]:leading-[140%] [&_.MuiOutlinedInput-root]:tracking-[0.02em] [&_.MuiOutlinedInput-root]:text-[#161A1F] [&_.MuiOutlinedInput-root]:transition-colors [&_.MuiOutlinedInput-root]:duration-200 [&_.MuiOutlinedInput-inputMultiline]:p-[14px] [&_.MuiOutlinedInput-inputMultiline]:resize-none [&_.MuiInputBase-input::placeholder]:text-[#7B8794] [&_.MuiInputBase-input::placeholder]:opacity-100 [&_.MuiOutlinedInput-notchedOutline]:border-2 [&_.MuiOutlinedInput-notchedOutline]:!border-[#D7DEE7] [&_.MuiOutlinedInput-root:hover_.MuiOutlinedInput-notchedOutline]:!border-[#005F73] [&_.MuiOutlinedInput-root.Mui-focused_.MuiOutlinedInput-notchedOutline]:!border-[#005F73] [&_.MuiOutlinedInput-root.Mui-focused:hover_.MuiOutlinedInput-notchedOutline]:!border-[#005F73]'
          />
        </Box>
      </Box>
    </BaseDeviceModal>
  );
};

export default memo(ExchangeDeviceModal);
